import { Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDuration } from '@/lib/format';
import { estaOcupado, textoDeFase } from '@/lib/softphone/estado';
import { ATRIBUTO_DEL_PANEL } from '@/lib/softphone/panel';
import { useSoftphone } from '@/lib/softphone/use-softphone';
import { cn } from '@/lib/utils';
import { ControlesDeLlamada } from './controles-de-llamada';

export function CabeceraDeLlamada({ nombreDeRespaldo }: { nombreDeRespaldo?: string }) {
  const { state, durationSeconds } = useSoftphone();
  const activa = estaOcupado(state);
  const enLlamada = state.fase === 'in_call';
  const marcando = activa && !enLlamada;
  const nombre = state.nombre || nombreDeRespaldo || state.telefono;
  const conReloj = state.fase === 'in_call' || state.contestadaEn !== null;

  return (
    <div className="flex items-center gap-3">
      <span className="relative grid size-9 shrink-0 place-items-center">
        {marcando && (
          <span aria-hidden className="absolute inset-0 rounded-full bg-success/40 motion-safe:animate-ping" />
        )}
        <span
          className={cn(
            'relative grid size-9 place-items-center rounded-full transition-colors duration-150 ease-out',
            enLlamada ? 'bg-success' : activa ? 'bg-success/15 ring-2 ring-success/40' : 'bg-muted',
          )}
        >
          <Phone
            aria-hidden
            className={cn(
              'size-4',
              enLlamada ? 'text-success-foreground' : activa ? 'text-success' : 'text-muted-foreground',
            )}
          />
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{nombre}</p>
        {state.telefono && nombre !== state.telefono && (
          <p className="truncate text-xs tabular-nums text-muted-foreground">{state.telefono}</p>
        )}
        <p aria-live="polite" className="text-xs tabular-nums text-muted-foreground">
          <span className={cn(enLlamada && 'font-medium text-success')}>{textoDeFase(state)}</span>
          {conReloj && ` · ${formatDuration(durationSeconds)}`}
        </p>
      </div>
    </div>
  );
}

export function PanelDeLlamada() {
  const { state, dismiss, panelReclamado } = useSoftphone();
  if (state.fase === 'idle' || panelReclamado) return null;
  const activa = estaOcupado(state);

  return (
    <div
      role="dialog"
      aria-label={`Llamada a ${state.nombre || state.telefono || 'contacto'}`}
      {...{ [ATRIBUTO_DEL_PANEL]: '' }}
      className="pointer-events-auto fixed bottom-4 right-4 z-[60] w-80 rounded-xl border border-border bg-background p-4 shadow-lg"
    >
      <CabeceraDeLlamada />
      <div className="mt-3">
        {activa ? (
          <ControlesDeLlamada />
        ) : (
          <Button variant="outline" className="w-full" onClick={dismiss}>
            Cerrar
          </Button>
        )}
      </div>
    </div>
  );
}
